import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { appointmentDetail, downloadInvoice } from "../../redux/reducers/AppoinmentSlice";
import Dashboard from "./Dashboard";
import Toast from "../../utils/toast";
import axios from "axios";
import { getToken } from "../../utils/localstorage/token";

const AppointmentDetails = ({ setActiveTab }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [appointmentData, setAppointmentData] = useState(null);
    const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);

    useEffect(() => {
        if (!id) return;

        const fetchAppointmentDetails = async () => {
            try {
                const response = await dispatch(appointmentDetail({ appointment_id: id }));
                if (response.payload) {
                    setAppointmentData(response.payload);
                    setActiveTab("appointments");
                } else {
                    // Error: No data received for appointment
                }
            } catch (error) {
                // Error fetching appointment details
            }
        };

        fetchAppointmentDetails();
    }, [dispatch, id]);



    if (!appointmentData) return <p>Loading...</p>;

    const { data } = appointmentData;
    if (!data) return <p>No appointment data available.</p>;

    const {
        appointment_id, patient_name, age, weight, phone_no, amount,
        payment_type, appointment_status, appointment_report_image,
        patient_address, drug_effect, note, illness_information,
        date, time, prescriptions, zoom_meeting_status, zoom_meeting_link,
        zoom_meeting_id, zoom_meeting_password, cancel_by, cancel_reason
    } = data;

    const handleDownload = async (fileUrl, fileName) => {
        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            // Create an anchor tag and trigger download
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            // Error downloading the file
        }
    };

    const handleDownloadInvoice = async () => {
        if (payment_type !== 'Razorpay') {
            Toast('info', 'Invoice is only available for paid appointments');
            return;
        }

        try {
            setIsDownloadingInvoice(true);

            // Get the API base URL and token
            const baseURL = process.env.REACT_APP_BASE_URL || 'https://teledoc.co.in/backend/public/api';
            const token = getToken();

            // Make API call to download invoice
            const response = await axios({
                url: `${baseURL}/download_invoice`,
                method: 'POST',
                data: { appointment_id: appointment_id },
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
                responseType: 'blob', // Important for file download
            });

            // Create a blob from the response
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice_${appointment_id.replace('#', '')}.pdf`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Toast('success', 'Invoice downloaded successfully!');
        } catch (error) {
            Toast('error', error?.response?.data?.message || 'Failed to download invoice. Please try again.');
        } finally {
            setIsDownloadingInvoice(false);
        }
    };

    // Handle patient joining meeting - calls API to track patient joined
    const handleJoinMeeting = async (appointmentId, meetingLink) => {
        try {
            const baseURL = process.env.REACT_APP_BASE_URL || 'https://teledoc.co.in/backend/public/api';
            const token = getToken();

            // Call API to mark patient as joined
            await axios({
                url: `${baseURL}/meeting_joined`,
                method: 'POST',
                data: {
                    appointment_id: appointmentId,
                    joined_by: 'patient'  // Track that PATIENT is joining
                },
                headers: {
                    'Authorization': token,
                    'Content-Type': 'application/json',
                },
            });

            // Open the meeting link after API call
            window.open(meetingLink, '_blank', 'noopener,noreferrer');
        } catch (error) {
            // Still open the meeting even if API call fails
            console.error('Failed to track meeting join:', error);
            window.open(meetingLink, '_blank', 'noopener,noreferrer');
        }
    };

    return (<>


        <div className="appointment-details">
            <h5>Appointment Details</h5>
            <div className="details-header">
                <div className="patient-info">
                    <p><strong>Appointment ID:</strong> {appointment_id}</p>
                    <p><strong>Patient Name:</strong> {patient_name}</p>
                    <p><strong>Patient Address:</strong> {patient_address}</p>
                    <p><strong>Patient Phone:</strong> {phone_no}</p>
                    <p><strong>Patient Age:</strong> {age}</p>
                    <p><strong>Patient Weight:</strong> {weight ?? '-'}</p>
                </div>
                <div className="doctor-info">
                    <p><strong>Date:</strong> {date} {time}</p>
                    <p><strong>Amount:</strong> ₹{amount}</p>
                    <p><strong>Payment Type:</strong> {payment_type}</p>
                    <p><strong>Payment Status:</strong> <span className="status paid">{appointment_status}</span></p>

                    {/* Download Invoice Button - Only for Razorpay payments */}
                    {payment_type === 'Razorpay' && (
                        <div style={{ marginTop: '15px' }}>
                            <button
                                onClick={handleDownloadInvoice}
                                disabled={isDownloadingInvoice}
                                style={{
                                    backgroundColor: '#6f42c1',
                                    color: 'white',
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: isDownloadingInvoice ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: isDownloadingInvoice ? 0.7 : 1,
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => !isDownloadingInvoice && (e.currentTarget.style.backgroundColor = '#5a2da8')}
                                onMouseOut={(e) => !isDownloadingInvoice && (e.currentTarget.style.backgroundColor = '#6f42c1')}
                            >
                                <i className="fas fa-download"></i>
                                {isDownloadingInvoice ? 'Downloading...' : 'Download Invoice'}
                            </button>
                        </div>
                    )}

                    {appointment_status === 'cancel' && cancel_by && cancel_reason && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                            <p style={{ margin: '0', color: '#721c24' }}><strong>Cancelled by:</strong> {cancel_by}</p>
                            <p style={{ margin: '0', color: '#721c24' }}><strong>Cancellation Reason:</strong> {cancel_reason}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Drug & Illness Information */}
            <div style={{ width: "100%", marginTop: "10px" }}>
                <p><strong>Any allergies or drug side effects?</strong> {drug_effect || "N/A"}</p>
                <p><strong>Briefly Describe The Problem:</strong> {note || "N/A"}</p>
                <p><strong>Illness Information:</strong> {illness_information || "N/A"}</p>
            </div>

            {/* Meeting Information */}
            {zoom_meeting_status === 1 && zoom_meeting_link && (
                <div style={{ width: "100%", marginTop: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                    <h4 style={{ color: "#007bff", marginBottom: "15px" }}>📹 Meeting Details</h4>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <p><strong>Meeting Link (Patient):</strong>
                            <button
                                onClick={() => handleJoinMeeting(appointment_id, zoom_meeting_link)}
                                style={{
                                    color: "white",
                                    textDecoration: "none",
                                    marginLeft: "10px",
                                    padding: "5px 15px",
                                    backgroundColor: "#007bff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "14px"
                                }}
                            >
                                Join Meeting
                            </button>
                        </p>
                        {zoom_meeting_id && (
                            <p><strong>Meeting ID:</strong> {zoom_meeting_id}</p>
                        )}
                        <p style={{ fontSize: "14px", color: "#6c757d", marginTop: "10px" }}>
                            <strong>Note:</strong> Please join the meeting 5 minutes before your scheduled appointment time.
                        </p>
                    </div>
                </div>
            )}

            {/* Report Images or PDFs */}
            {Array.isArray(appointment_report_image) && appointment_report_image.length > 0 && (
                <>
                    <h3>Patient Report</h3>
                    <div className="report-container">
                        <div className="report-grid">
                            {appointment_report_image.map((img, index) => {
                                const isPDF = img.endsWith(".pdf");
                                const fileName = `report_${index + 1}${isPDF ? ".pdf" : ".png"}`;

                                return (
                                    <div key={index} className="report-card">
                                        {isPDF ? (
                                            <div className="pdf-container">
                                                <a href={img} target="_blank" rel="noopener noreferrer" className="view-pdf-btn">
                                                    View PDF
                                                </a>
                                            </div>
                                        ) : (
                                            <img src={img} alt="Patient Report" className="report-thumbnail" />
                                        )}

                                        {/* ✅ Download Button अब हमेशा सबसे नीचे आएगा */}
                                        <div className="download-btn-container">
                                            <button className="download-btn" onClick={() => handleDownload(img, fileName)}>
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}


            {/* Prescription Table */}
            <h3>Prescription</h3>
            <table className="prescription-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th colSpan="3">Prescription</th>
                        <th>Test Name</th>
                        <th>View</th>
                    </tr>
                </thead>
                <tbody>
                    {prescriptions && prescriptions.length > 0 ? (
                        prescriptions.map((prescription, index) => (
                            <tr key={index}>
                                <td>{new Date(prescription.created_at).toDateString()}</td>
                                <td colSpan="3">
                                    <div className="table-container">
                                        <table className="inner-table">
                                            <thead>
                                                <tr>
                                                    <th>Medicine</th>
                                                    <th>Days</th>
                                                    <th>Time (Morning - Afternoon - Night)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {prescription.prescription.map((med, i) => (
                                                    <tr key={`${index}-${i}`}>
                                                        <td>{med.medicine}</td>
                                                        <td>{med.days}</td>
                                                        <td>{med.morning} - {med.afternoon} - {med.night}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                                <td>
                                    {prescription.test_report && prescription.test_report.length > 0
                                        ? prescription.test_report.map(test => test.pathology_name).join(", ")
                                        : "-"}
                                </td>
                                <td>
                                    <a href={prescription.pdf} target="_blank" rel="noopener noreferrer" className="view-btn">
                                        View
                                    </a>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="5">No prescriptions available.</td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Doctor's Notes */}
            <div style={{ marginTop: "15px" }}>
                <h3>Doctor Note</h3>
                <p>
                    {prescriptions && prescriptions.length > 0
                        ? prescriptions[0].note
                        : "No notes available."}
                </p>
            </div>

        </div>
    </>);
};

export default AppointmentDetails;
